import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/common/Layout';
import { shelfService } from '../services/shelfService';
import { bookService } from '../services/bookService';
import type { Shelf, ShelfCreate, ShelfUpdate, Book } from '../types';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { Notification } from '../components/common/Notification';
import './ShelvesPage.css';

export const ShelvesPage = () => {
  const navigate = useNavigate();
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBooksModal, setShowBooksModal] = useState(false);
  const [showAssignShelfModal, setShowAssignShelfModal] = useState(false);
  const [showAddBooksModal, setShowAddBooksModal] = useState(false);
  const [selectedShelf, setSelectedShelf] = useState<Shelf | null>(null);
  const [shelfToDelete, setShelfToDelete] = useState<Shelf | null>(null);
  const [bookToAssign, setBookToAssign] = useState<Book | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

  useEffect(() => {
    loadShelves();
    loadBooks();
  }, []);

  const loadShelves = async () => {
    try {
      const shelvesData = await shelfService.getAllShelvesIncludingEmpty({ limit: 1000 });
      setShelves(shelvesData);
    } catch (error) {
      console.error('Error loading shelves:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadBooks = async () => {
    try {
      const booksData = await bookService.getAllBooks({ limit: 1000 });
      setBooks(booksData);
    } catch (error) {
      console.error('Error loading books:', error);
    }
  };

  const handleCreateShelf = async (shelfData: ShelfCreate | ShelfUpdate) => {
    if (!('shelf_number' in shelfData) || !shelfData.shelf_number) return;
    
    try {
      await shelfService.createShelf({ shelf_number: shelfData.shelf_number, description: shelfData.description });
      setNotification({
        type: 'success',
        title: 'Shelf Created',
        message: `Shelf "${shelfData.shelf_number}" has been created successfully.`,
      });
      setShowCreateModal(false);
      loadShelves();
    } catch (error: any) {
      setNotification({
        type: 'error',
        title: 'Error Creating Shelf',
        message: error.response?.data?.detail || 'Failed to create shelf',
      });
    }
  };

  const handleEditShelf = async (shelfData: ShelfUpdate) => {
    if (!selectedShelf) return;
    try {
      await shelfService.updateShelf(selectedShelf.id, shelfData);
      setNotification({
        type: 'success',
        title: 'Shelf Updated',
        message: `Shelf "${shelfData.shelf_number || selectedShelf.shelf_number}" has been updated successfully.`,
      });
      setShowEditModal(false);
      setSelectedShelf(null);
      loadShelves();
    } catch (error: any) {
      setNotification({
        type: 'error',
        title: 'Error Updating Shelf',
        message: error.response?.data?.detail || 'Failed to update shelf',
      });
    }
  };

  const handleDeleteShelf = async () => {
    if (!shelfToDelete) return;
    if (shelfToDelete.book_count > 0) {
      setNotification({
        type: 'warning',
        title: 'Cannot Delete Shelf',
        message: `Cannot delete shelf "${shelfToDelete.shelf_number}" because it contains ${shelfToDelete.book_count} book(s). Please remove all books first.`,
      });
      setShelfToDelete(null);
      setShowConfirmModal(false);
      return;
    }
    
    try {
      await shelfService.deleteShelf(shelfToDelete.id);
      setNotification({
        type: 'success',
        title: 'Shelf Deleted',
        message: `Shelf "${shelfToDelete.shelf_number}" has been deleted successfully.`,
      });
      setShowConfirmModal(false);
      setShelfToDelete(null);
      loadShelves();
    } catch (error: any) {
      setNotification({
        type: 'error',
        title: 'Error Deleting Shelf',
        message: error.response?.data?.detail || 'Failed to delete shelf',
      });
    }
  };

  const handleAssignShelfToBook = async (shelfId: number | null) => {
    if (!bookToAssign) return;
    try {
      await bookService.updateBookShelf(bookToAssign.id, shelfId);
      setNotification({
        type: 'success',
        title: 'Shelf Assigned',
        message: shelfId 
          ? `Book "${bookToAssign.title}" has been assigned to shelf ${shelfId}.`
          : `Book "${bookToAssign.title}" has been removed from shelf.`,
      });
      setShowAssignShelfModal(false);
      setBookToAssign(null);
      loadShelves();
      loadBooks();
    } catch (error: any) {
      setNotification({
        type: 'error',
        title: 'Error Assigning Shelf',
        message: error.response?.data?.detail || 'Failed to assign shelf',
      });
    }
  };

  const handleViewBooks = (shelf: Shelf) => {
    setSelectedShelf(shelf);
    setShowBooksModal(true);
  };

  const shelfSortOrder = (a: Shelf, b: Shelf): number => {
    const sortOrder: Record<string, Record<string, number>> = {
      'A': { '1': 1, '2': 2 },
      'B': { '1': 1, '2': 2 },
      'C': { '1': 1, '2': 2 },
      'D': { '1-1': 1, '2': 2, '1-2': 3 },
      'E': { '1': 1 },
    };
    const getKey = (shelfNumber: string) => {
      const match = shelfNumber.match(/^([A-Z])-(\d+)(?:-(\d+))?$/i);
      if (!match) return null;
      const letter = match[1].toUpperCase();
      const num1 = match[2];
      const num2 = match[3] || null;
      return { letter, num1, num2 };
    };
    const aKey = getKey(a.shelf_number);
    const bKey = getKey(b.shelf_number);
    if (!aKey || !bKey) return a.shelf_number.localeCompare(b.shelf_number);
    if (aKey.letter !== bKey.letter) return aKey.letter.localeCompare(bKey.letter);
    const order = sortOrder[aKey.letter];
    if (order) {
      const aSubKey = aKey.num2 ? `${aKey.num1}-${aKey.num2}` : aKey.num1;
      const bSubKey = bKey.num2 ? `${bKey.num1}-${bKey.num2}` : bKey.num1;
      const aIdx = order[aSubKey];
      const bIdx = order[bSubKey];
      if (aIdx !== undefined && bIdx !== undefined) {
        return aIdx - bIdx;
      }
      if (aIdx !== undefined) return -1;
      if (bIdx !== undefined) return 1;
    }
    if (aKey.num1 !== bKey.num1) return parseInt(aKey.num1) - parseInt(bKey.num1);
    if (aKey.num2 !== null && bKey.num2 !== null) return parseInt(aKey.num2) - parseInt(bKey.num2);
    if (aKey.num2 !== null) return 1;
    if (bKey.num2 !== null) return -1;
    return 0;
  };

  const displayedShelves = shelves.filter(shelf =>
    shelf.shelf_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (shelf.description && shelf.description.toLowerCase().includes(searchTerm.toLowerCase()))
  ).sort(shelfSortOrder);

  const getBooksForShelf = (shelfId: number) => {
    return books.filter(book => book.shelf_id === shelfId);
  };

  if (isLoading) {
    return (
      <Layout isAdmin>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading shelves...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout isAdmin>
      <div className="shelves-page">
        {notification && (
          <Notification
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}

        <div className="page-header">
          <h2 className="page-title">Shelf Management</h2>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <span className="material-symbols-outlined">add</span>
            Add Shelf
          </button>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">All Shelves</h3>
            <div className="header-actions">
              <div className="search-wrapper">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  className="form-input search-input"
                  placeholder="Search shelves..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="view-toggle">
                <button
                  className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                >
                  <span className="material-symbols-outlined">grid_view</span>
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'table' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setViewMode('table')}
                  title="Table view"
                >
                  <span className="material-symbols-outlined">view_week</span>
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'grid' ? (
            <div className="shelf-grid">
              {displayedShelves.length === 0 ? (
                <div className="empty-state">No shelves found. Click "Add Shelf" to create one.</div>
              ) : (
                displayedShelves.map((shelf) => (
                  <div key={shelf.id} className="shelf-card" onClick={() => handleViewBooks(shelf)}>
                    <div className="shelf-card-header">
                      <span className="shelf-number">{shelf.shelf_number}</span>
                      <div className="shelf-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => {
                            setSelectedShelf(shelf);
                            setShowEditModal(true);
                          }}
                          title="Edit shelf"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShelfToDelete(shelf);
                            setShowConfirmModal(true);
                          }}
                          title="Delete shelf"
                          disabled={shelf.book_count > 0}
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </div>
                    <div className="shelf-card-body">
                      <p className="shelf-description">{shelf.description || 'No description'}</p>
                      <span className={`badge ${shelf.book_count > 0 ? 'badge-success' : 'badge-secondary'}`}>
                        {shelf.book_count} {shelf.book_count === 1 ? 'book' : 'books'}
                      </span>
                    </div>
                    <div className="shelf-card-footer">
                      <span className="text-sub">Created: {new Date(shelf.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Shelf Number</th>
                    <th>Description</th>
                    <th>Book Count</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedShelves.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        No shelves found. Click "Add Shelf" to create one.
                      </td>
                    </tr>
                  ) : (
                    displayedShelves.map((shelf) => (
                      <tr key={shelf.id} onClick={() => handleViewBooks(shelf)} style={{ cursor: 'pointer' }}>
                        <td>
                          <strong>{shelf.shelf_number}</strong>
                        </td>
                        <td>{shelf.description || '-'}</td>
                        <td>
                          <span className={`badge ${shelf.book_count > 0 ? 'badge-success' : 'badge-secondary'}`}>
                            {shelf.book_count} {shelf.book_count === 1 ? 'book' : 'books'}
                          </span>
                        </td>
                        <td className="text-sub">{new Date(shelf.created_at).toLocaleDateString()}</td>
                        <td className="text-sub">{new Date(shelf.updated_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleViewBooks(shelf)}
                              title="View/Add books"
                            >
                              <span className="material-symbols-outlined">menu_book</span>
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => {
                                setSelectedShelf(shelf);
                                setShowEditModal(true);
                              }}
                              title="Edit shelf"
                            >
                              <span className="material-symbols-outlined">edit</span>
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShelfToDelete(shelf);
                                setShowConfirmModal(true);
                              }}
                              title="Delete shelf"
                              disabled={shelf.book_count > 0}
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Shelf Modal */}
        {showCreateModal && (
          <ShelfFormModal
            mode="create"
            onClose={() => setShowCreateModal(false)}
            onSaved={handleCreateShelf}
            onError={(error) =>
              setNotification({
                type: 'error',
                title: error.title,
                message: error.message,
              })
            }
          />
        )}

        {/* Edit Shelf Modal */}
        {showEditModal && selectedShelf && (
          <ShelfFormModal
            mode="edit"
            shelf={selectedShelf}
            onClose={() => {
              setShowEditModal(false);
              setSelectedShelf(null);
            }}
            onSaved={handleEditShelf}
            onError={(error) =>
              setNotification({
                type: 'error',
                title: error.title,
                message: error.message,
              })
            }
          />
        )}

        {/* View Books Modal */}
        {showBooksModal && selectedShelf && (
          <ShelfBooksModal
            shelf={selectedShelf}
            books={getBooksForShelf(selectedShelf.id)}
            allShelves={shelves}
            onClose={() => {
              setShowBooksModal(false);
              setSelectedShelf(null);
            }}
            onAssignShelf={(book) => {
              setBookToAssign(book);
              setShowAssignShelfModal(true);
            }}
            onAddBooks={() => {
              setShowAddBooksModal(true);
            }}
          />
        )}

        {/* Assign Shelf Modal */}
        {showAssignShelfModal && bookToAssign && (
          <AssignShelfModal
            book={bookToAssign}
            currentShelfId={bookToAssign.shelf_id}
            shelves={shelves}
            onClose={() => {
              setShowAssignShelfModal(false);
              setBookToAssign(null);
            }}
            onAssign={handleAssignShelfToBook}
          />
        )}

        {/* Add Books Modal */}
        {showAddBooksModal && selectedShelf && (
          <AddBooksModal
            shelf={selectedShelf}
            books={books}
            onClose={() => {
              setShowAddBooksModal(false);
              setSelectedShelf(null);
            }}
            onAdded={() => {
              setShowAddBooksModal(false);
              loadShelves();
              loadBooks();
            }}
            onError={(error) =>
              setNotification({
                type: 'error',
                title: error.title,
                message: error.message,
              })
            }
          />
        )}

        {/* Confirm Delete Modal */}
        {shelfToDelete && shelfToDelete.book_count === 0 && (
          <ConfirmModal
            title="Delete Shelf"
            message={`Are you sure you want to delete shelf "${shelfToDelete.shelf_number}"?`}
            warningMessage="This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            isDanger
            onConfirm={handleDeleteShelf}
            onCancel={() => {
              setShowConfirmModal(false);
              setShelfToDelete(null);
            }}
          />
        )}
      </div>
    </Layout>
  );
};

// Shelf Form Modal Component
interface ShelfFormModalProps {
  mode: 'create' | 'edit';
  shelf?: Shelf;
  onClose: () => void;
  onSaved: (shelfData: ShelfCreate | ShelfUpdate) => void;
  onError: (error: { type: 'error'; title: string; message: string }) => void;
}

const ShelfFormModal = ({ mode, shelf, onClose, onSaved, onError }: ShelfFormModalProps) => {
  const [formData, setFormData] = useState<{ shelf_number: string; description: string }>({
    shelf_number: shelf?.shelf_number || '',
    description: shelf?.description || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.shelf_number?.trim()) {
      onError({
        type: 'error',
        title: 'Validation Error',
        message: 'Shelf number is required',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSaved(formData);
    } catch (error: any) {
      onError({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to save shelf',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>{mode === 'create' ? 'Create Shelf' : 'Edit Shelf'}</h3>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="shelf_number">Shelf Number *</label>
              <input
                id="shelf_number"
                type="text"
                className="form-input"
                value={formData.shelf_number}
                onChange={(e) => setFormData({ ...formData, shelf_number: e.target.value })}
                placeholder="e.g., A-1, B-2, Floor 1"
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="form-input"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description for this shelf"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Shelf' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Shelf Books Modal Component
interface ShelfBooksModalProps {
  shelf: Shelf;
  books: Book[];
  allShelves: Shelf[];
  onClose: () => void;
  onAssignShelf: (book: Book) => void;
  onAddBooks: () => void;
}

const ShelfBooksModal = ({ shelf, books, allShelves, onClose, onAssignShelf, onAddBooks }: ShelfBooksModalProps) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Books on Shelf {shelf.shelf_number}</h3>
          <div className="modal-header-actions">
            <button className="btn btn-primary btn-sm" onClick={onAddBooks}>
              <span className="material-symbols-outlined">add</span>
              Add Books
            </button>
            <button className="modal-close" onClick={onClose}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>
        <div className="modal-body">
          {shelf.description && (
            <p className="shelf-description">{shelf.description}</p>
          )}
          <p className="shelf-book-count">{books.length} {books.length === 1 ? 'book' : 'books'} on this shelf</p>

          {books.length === 0 ? (
            <div className="empty-state">
              <p>No books on this shelf yet.</p>
              <button className="btn btn-primary" onClick={onAddBooks}>
                <span className="material-symbols-outlined">add</span>
                Add Your First Book
              </button>
            </div>
          ) : (
            <div className="books-grid">
              {books.map((book) => (
                <div key={book.id} className="book-card-mini">
                  <div className="book-card-header">
                    <strong>{book.book_id}</strong>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => onAssignShelf(book)}
                      title="Change shelf"
                    >
                      <span className="material-symbols-outlined">move_item</span>
                    </button>
                  </div>
                  <p className="book-card-title">{book.title}</p>
                  <p className="book-card-author">{book.author_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Add Books to Shelf Modal Component
interface AddBooksModalProps {
  shelf: Shelf;
  books: Book[];
  onClose: () => void;
  onAdded: () => void;
  onError: (error: { type: 'error'; title: string; message: string }) => void;
}

const AddBooksModal = ({ shelf, books, onClose, onAdded, onError }: AddBooksModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBookIds, setSelectedBookIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter books that are not assigned to any shelf
  const availableBooks = books.filter(book => book.shelf_id === null || book.shelf_id === undefined);

  // Filter books based on search
  const filteredBooks = availableBooks.filter(book =>
    book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.book_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (book.isbn && book.isbn.includes(searchTerm))
  );

  const handleBookToggle = (bookId: number) => {
    setSelectedBookIds(prev =>
      prev.includes(bookId)
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBookIds.length === filteredBooks.length) {
      setSelectedBookIds([]);
    } else {
      setSelectedBookIds(filteredBooks.map(b => b.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedBookIds.length === 0) {
      onError({
        type: 'error',
        title: 'No Books Selected',
        message: 'Please select at least one book to add to the shelf.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await Promise.all(
        selectedBookIds.map(bookId => bookService.updateBookShelf(bookId, shelf.id))
      );

      onAdded();
    } catch (error: any) {
      onError({
        type: 'error',
        title: 'Error Adding Books',
        message: error.response?.data?.detail || 'Failed to add books to shelf',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Books to Shelf {shelf.shelf_number}</h3>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="add-books-search">
            <div className="search-wrapper" style={{ flex: 1 }}>
              <span className="material-symbols-outlined">search</span>
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search books by title, author, book ID, or ISBN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleSelectAll}
              disabled={filteredBooks.length === 0}
            >
              {selectedBookIds.length === filteredBooks.length && filteredBooks.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>

          <div className="selected-count">
            {selectedBookIds.length} book{selectedBookIds.length !== 1 ? 's' : ''} selected
          </div>

          <div className="books-list">
            {filteredBooks.length === 0 ? (
              <div className="empty-state">
                {availableBooks.length === 0
                  ? 'All books are already assigned to shelves or no books available.'
                  : 'No books match your search.'}
              </div>
            ) : (
              filteredBooks.map((book) => (
                <div
                  key={book.id}
                  className={`book-list-item ${selectedBookIds.includes(book.id) ? 'selected' : ''}`}
                  onClick={() => handleBookToggle(book.id)}
                >
                  <div className="book-list-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedBookIds.includes(book.id)}
                      onChange={() => handleBookToggle(book.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="book-list-info">
                    <div className="book-list-title">{book.title}</div>
                    <div className="book-list-meta">
                      {book.author_name} • {book.book_id}
                      {book.isbn ? ` • ISBN: ${book.isbn}` : ''}
                    </div>
                  </div>
                  <div className="book-list-shelf">
                    Unassigned
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={selectedBookIds.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Adding...' : `Add ${selectedBookIds.length} Book${selectedBookIds.length !== 1 ? 's' : ''} to Shelf`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Assign Shelf Modal Component
interface AssignShelfModalProps {
  book: Book;
  currentShelfId: number | null;
  shelves: Shelf[];
  onClose: () => void;
  onAssign: (shelfId: number | null) => void;
}

const AssignShelfModal = ({ book, currentShelfId, shelves, onClose, onAssign }: AssignShelfModalProps) => {
  const [selectedShelfId, setSelectedShelfId] = useState<number | ''>(currentShelfId || '');

  const handleSubmit = () => {
    onAssign(selectedShelfId === '' ? null : selectedShelfId);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Assign Shelf to "{book.title}"</h3>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="shelf">Select Shelf</label>
            <select
              id="shelf"
              className="form-input"
              value={selectedShelfId}
              onChange={(e) => setSelectedShelfId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">No Shelf (Unassigned)</option>
              {shelves.map((shelf) => (
                <option key={shelf.id} value={shelf.id}>
                  {shelf.shelf_number} {shelf.id === currentShelfId ? '(Current)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>
            Assign Shelf
          </button>
        </div>
      </div>
    </div>
  );
};
